import {
  EligibleRegisterGroup,
  RegisterComposition,
  useCreateAttendanceRegisterMutation,
  useDeleteAttendanceRegisterMutation,
  useEditAttendanceRegisterMutation,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRouter } from '../../../context/router/useRouter';
import { RegisterCreateForm } from './useRegisterComposition';
import { RegisterGroupIds, RegisterPeriod } from './types';
import { getRegisterEntriesCount } from './utils/registerCounts';
import { getAutoSelectedGroupIds, hasSelectedGroups } from './utils/registerGroupIds';
import { buildExplicitlyRemovedPayload } from './utils/registerRoster';

type UseRegisterSubmitParams = {
  form: UseFormReturn<RegisterCreateForm>;
  isEdit: boolean;
  registerId?: string;
  date: string;
  composition: RegisterComposition | undefined;
  eligibleGroups: EligibleRegisterGroup[];
  hasManualSelection: boolean;
  initialRosterIds: RegisterGroupIds;
  periods: RegisterPeriod[];
};

export const useRegisterSubmit = ({
  form,
  isEdit,
  registerId,
  date,
  composition,
  eligibleGroups,
  hasManualSelection,
  initialRosterIds,
  periods,
}: UseRegisterSubmitParams) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const { goBack } = useRouter();
  const navigate = useNavigate();
  const location = useLocation();
  const { getConfirmation } = useConfirmationDialog();
  const { showNotification, showError } = useNotifications();
  const invalidateListQueries = useInvalidateListQueriesFor('attendance');

  const createAttendanceRegister = useCreateAttendanceRegisterMutation();
  const editAttendanceRegister = useEditAttendanceRegisterMutation();
  const deleteAttendanceRegister = useDeleteAttendanceRegisterMutation();

  const buildGroupIdsPayload = useCallback(
    (groupIds: RegisterGroupIds) => ({
      am: groupIds.am,
      pm: groupIds.pm,
      periods: Object.entries(groupIds.periods).map(([period_id, group_ids]) => ({
        period_id,
        group_ids,
      })),
      explicitly_removed: buildExplicitlyRemovedPayload({
        eligibleGroups,
        selectedGroupIds: groupIds,
        initialRosterIds,
        isEdit,
        periods,
      }),
    }),
    [eligibleGroups, initialRosterIds, isEdit, periods],
  );

  const submitEdit = useCallback(
    (groupIds: RegisterGroupIds) => {
      if (!registerId || !date) return;

      editAttendanceRegister.mutate(
        {
          id: registerId,
          register: {
            register_date: date,
            group_ids: buildGroupIdsPayload(groupIds),
          },
          confirmed: true,
        },
        {
          onSuccess: () => {
            invalidateListQueries();
            goBack();
            showNotification({
              textId: 'attendance-RegisterUpdated',
              type: 'success',
            });
          },
          onError: showError,
        },
      );
    },
    [
      registerId,
      date,
      editAttendanceRegister,
      buildGroupIdsPayload,
      goBack,
      showNotification,
      invalidateListQueries,
      showError,
    ],
  );

  const submit = form.handleSubmit((data) => {
    if (!schoolId || !data.register_date) return;

    const groupIds =
      isEdit || hasManualSelection ? data.group_ids : getAutoSelectedGroupIds(eligibleGroups);

    if (!hasSelectedGroups(groupIds)) {
      showError({
        message: formatMessage({
          id: isEdit ? 'attendance-EmptyRegisterUpdateError' : 'attendance-EmptyRegisterError',
        }),
      });

      return;
    }

    if (isEdit) {
      submitEdit(groupIds);
      return;
    }

    createAttendanceRegister.mutate(
      {
        schoolId,
        register: {
          name: formatMessage({ id: 'attendance-Register' }),
          register_date: data.register_date,
          group_ids: buildGroupIdsPayload(groupIds),
        },
      },
      {
        onSuccess: (register) => {
          goBack();
          showNotification({
            textId: 'attendance-RegisterCreated',
            type: 'success',
            actions: [
              {
                textId: 'attendance-ViewRegister',
                handler: () => navigate(`/attendance/${register.id}`, { state: location.state }),
                buttonColor: 'light',
              },
            ],
          });
          invalidateListQueries();
        },
        onError: showError,
      },
    );
  });

  const remove = useCallback(async () => {
    if (!isEdit || !registerId) return;

    const entriesCount = composition ? getRegisterEntriesCount(composition) : 0;
    if (entriesCount > 0) {
      const confirmed = await getConfirmation({
        textId: 'attendance-RegisterDeleteConfirmation',
        textValues: { entriesCount },
      });
      if (!confirmed) return;
    }

    deleteAttendanceRegister.mutate(
      { id: registerId, confirmed: true },
      {
        onSuccess: () => {
          navigate('/attendance');
          showNotification({
            textId: 'attendance-RegisterDeleted',
            type: 'success',
          });
          invalidateListQueries();
        },
        onError: showError,
      },
    );
  }, [
    isEdit,
    registerId,
    composition,
    getConfirmation,
    deleteAttendanceRegister,
    navigate,
    showNotification,
    invalidateListQueries,
    showError,
  ]);

  return {
    submit,
    remove,
    saving: createAttendanceRegister.isLoading || editAttendanceRegister.isLoading,
    deleting: deleteAttendanceRegister.isLoading,
  };
};
